import { ShopAvailabilityHeader, useShopAvailability } from "../../components/shopAvailability";
import { ActiveOrders } from "../../components/activeOrders";
import { ShopInstall } from "../../components/shopInstall";
import { useProductModal } from "../../components/productModal/context";
import { useCartDrawer } from "../../components/cartDrawer/context";
import { useEffect, useMemo, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { LuPlus, LuArrowRight, LuSearch } from "react-icons/lu";
import { setSearch } from "../../reducers/userConfigSlice";
import { productPath } from "./productPath";
import { formatMoney } from "../../functions/money";
import { StoreDelivery } from "../../components/storeDelivery";
import { ProductImage } from "../../components/productImage";
import { useCartAmounts } from "../../hooks/useCartAmounts";
import "./style.css";

const normalize = value => (value || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

const CatalogueProduct = ({ product, featured = false }) => {
  const { openProduct } = useProductModal();
  const { canOrder } = useShopAvailability();
  const lines = useSelector(state => state.aplicationConfig.userConfig.cart?.ShoppingCartProducts || []);
  const quantity = lines.filter(line => (line.productId || line.Product?.productId) === product.productId).reduce((sum, line) => sum + Number(line.quantity || 0), 0);
  const available = Number.isFinite(product.priceWithTax);
  const actions = <div className="eats-product-actions">
    {quantity > 0 && <span className="eats-product-quantity" aria-label={`${quantity} unidades en tu carrito`}>{quantity}</span>}
    <button type="button" className="eats-add" disabled={!canOrder} title={!canOrder ? "Pedidos no disponibles en este momento" : undefined} aria-label={`Elegir ${product.name}`} onClick={() => openProduct(productPath(product))}><LuPlus aria-hidden="true" /></button>
  </div>;
  return <article className={`eats-product ${featured ? "eats-product-featured" : ""}`}>
    <div className="eats-product-photo-wrap">
      <button type="button" className="eats-product-photo" aria-label={`Ver detalles de ${product.name}`} onClick={() => openProduct(productPath(product))}>
        <ProductImage src={product.images?.[0]} name={product.name} />
      </button>
      {!featured && actions}
    </div>
    <div className="eats-product-copy">
      <h3><button type="button" onClick={() => openProduct(productPath(product))}>{product.name}</button></h3>
      <p>{product.description}</p>
      <div className="eats-product-footer">
        <div className="eats-price">{available ? formatMoney(product.priceWithTax) : "Precio no disponible"}</div>
        {featured && actions}
      </div>
    </div>
  </article>;
};

const CartBar = () => {
  const { openCart } = useCartDrawer();
  const lines = useSelector(state => state.aplicationConfig.userConfig.cart?.ShoppingCartProducts || []);
  const { amounts, busy, error } = useCartAmounts();
  const quantity = lines.reduce((sum, line) => sum + Number(line.quantity || 0), 0);
  if (!quantity) return null;
  return <div className="eats-cart-bar">
    <button type="button" onClick={openCart}>
      <span className="eats-bar-count">{quantity}</span><span>Ver carrito</span><span>{busy ? "Calculando…" : error || !amounts ? "Revisar importe" : formatMoney(amounts.total)}</span><LuArrowRight aria-hidden="true" />
    </button>
  </div>;
};

export const Products = () => {
  const { category: categoryParam } = useParams();
  const dispatch = useDispatch();
  const { products = [], categories = [], info } = useSelector(state => state.aplicationConfig.corporationData);
  const search = useSelector(state => state.aplicationConfig.userConfig.search);
  const [selected, setSelected] = useState("menu");
  const catalogueRef = useRef(null);
  const tabsRef = useRef(null);
  const visible = useMemo(() => products.filter(product => normalize(product.name).includes(normalize(search))), [products, search]);
  const sections = useMemo(() => search ? [] : categories.filter(category => category.isActive && visible.some(product => product.Categories?.some(cat => cat.categoryId === category.id))), [categories, visible, search]);
  useEffect(() => {
    let frame = null;
    const updateActive = () => {
      frame = null;
      const nodes = [...(catalogueRef.current?.querySelectorAll(".eats-menu-section") || [])];
      if (!nodes.length) return;
      const boundary = (tabsRef.current?.parentElement.getBoundingClientRect().bottom || 122) + 8;
      let active = nodes[0].id;
      for (const node of nodes) {
        if (node.getBoundingClientRect().top <= boundary) active = node.id;
        else break;
      }
      // Short final categories may never reach the sticky navigation boundary.
      const pageHeight = document.documentElement.scrollHeight;
      if (window.scrollY > 0 && pageHeight > 0 && window.scrollY + window.innerHeight >= pageHeight - 2) active = nodes[nodes.length - 1].id;
      setSelected(active);
    };
    const schedule = () => { if (frame === null) frame = window.requestAnimationFrame(updateActive); };
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    schedule();
    return () => {
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      if (frame !== null) window.cancelAnimationFrame(frame);
    };
  }, [sections, search]);
  useEffect(() => {
    const tabs = tabsRef.current;
    const active = [...(tabs?.querySelectorAll("button") || [])].find(button => button.dataset.categoryId === selected);
    if (!tabs || !active || tabs.scrollWidth <= tabs.clientWidth) return;
    const bounds = tabs.getBoundingClientRect();
    const button = active.getBoundingClientRect();
    if (button.left < bounds.left + 4) tabs.scrollTo({ left: tabs.scrollLeft + button.left - bounds.left - 4, behavior: "smooth" });
    else if (button.right > bounds.right - 4) tabs.scrollTo({ left: tabs.scrollLeft + button.right - bounds.right + 4, behavior: "smooth" });
  }, [selected]);
  const goTo = id => {
    setSelected(id);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  useEffect(() => {
    if (categoryParam) {
      const category = sections.find(item => normalize(item.name) === normalize(categoryParam));
      if (category) { setSelected(`category-${category.id}`); document.getElementById(`category-${category.id}`)?.scrollIntoView({ block: "start" }); }
    }
  }, [categoryParam, sections]);
  return <main ref={catalogueRef} className="mainContainer eats-catalogue">
    <section className="eats-store-intro">
      <div className="eats-title-row"><h1>{info?.corporation?.name || "Nuestra tienda"}</h1><ShopAvailabilityHeader /><ShopInstall compact /></div>
      <p>Elige tus favoritos y personaliza tu pedido.</p>
      <StoreDelivery><ActiveOrders /></StoreDelivery>
      <label className="eats-search eats-mobile-search"><LuSearch aria-hidden="true" /><input type="search" aria-label="Buscar productos" placeholder="Buscar productos…" value={search || ""} onChange={event => dispatch(setSearch(event.target.value))} /></label>
    </section>
    <nav className="productsMenu eats-category-nav" aria-label="Categorías de productos"><div ref={tabsRef} className="eats-nav-inner">
      <button type="button" data-category-id="menu" aria-pressed={selected === "menu"} onClick={() => goTo("menu")}>Nuestra carta</button>
      {sections.map(category => <button type="button" key={category.id} data-category-id={`category-${category.id}`} aria-pressed={selected === `category-${category.id}`} onClick={() => goTo(`category-${category.id}`)}>{category.name}</button>)}
    </div></nav>
    <div className="eats-menu-content">
      <section id="menu" className="eats-menu-section">
        <h2>{search ? "Resultados de búsqueda" : "Nuestra carta"}</h2>
        <p className="eats-section-subtitle">{search ? `Productos que coinciden con «${search}»` : "Todo listo para elegir a tu gusto"}</p>
        <div className="eats-featured-grid">{(search ? visible : visible.slice(0, 4)).map(product => <CatalogueProduct key={product.productId} product={product} featured />)}</div>
        {!visible.length && <p role="status" className="eats-empty">{search ? "No hay productos que coincidan con tu búsqueda." : "Todavía no hay productos disponibles."}</p>}
      </section>
      {sections.map(category => <section key={category.id} id={`category-${category.id}`} className="eats-menu-section">
        <h2>{category.name}</h2>
        <div className="eats-list-grid">{visible.filter(product => product.Categories?.some(cat => cat.categoryId === category.id)).map(product => <CatalogueProduct key={product.productId} product={product} />)}</div>
      </section>)}
      <footer className="eats-store-footer">Precios con IVA incluido · Puedes revisar tu pedido antes de confirmarlo.</footer>
    </div>
    <CartBar />
  </main>;
};
